/**
 * Brombeere — Hexenmeister (Brombeus). LMAO-Style (BATTLE-17):
 * "finsterer Dornbusch-Warlock" — kompakter, leicht bedrohlicher Klumpen aus
 * einzelnen Beeren-Noppen. Jede Noppe ist eine eigene Kugel mit Volumen-Gradient
 * und EINEM Glanzpunkt (die Beeren-Textur-Signatur); zwischen den Noppen bleiben
 * tiefe Fugen-Schatten sichtbar. Oben ein gezackter Hexen-Kelch mit Stiel-Haken.
 * 2★: Dornen brechen die Silhouette (Hexen-Dornbusch, docs/12 §1).
 * 3★/castPulse: violettes Schatten-Gluehen (KEYWORD shadow #9a6dd7) leckt aus
 * den Fugen zwischen den Noppen — nie auf den Kugeln selbst (docs/13 §5).
 * Referenz-Qualitaet: kohlrabi.ts / okra.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, specular, volumeGradient } from '../draw3d'
import { KEYWORD_COLORS } from '../../ui/theme'

/** verbindliche Schatten-Farbe (docs/12 §2) — nur fuer Glows, nie fuer den Koerper */
const SHADOW = KEYWORD_COLORS.shadow

/**
 * Deterministisches Noppen-Layout [dx, dy, br, gloss] in Einheits-Radius.
 * Reihenfolge = Zeichenreihenfolge: hinten/oben zuerst, vorne/unten zuletzt.
 * Letzter Eintrag = grosse ruhige "Gesichts-Noppe" (Renderer-Gesicht bei y-0.18r).
 */
const DRUPELETS: ReadonlyArray<readonly [number, number, number, number]> = [
  [-0.50, -0.52, 0.30, 0.85],
  [0.00, -0.74, 0.29, 1.0],
  [0.48, -0.55, 0.30, 0.9],
  [-0.72, -0.06, 0.31, 0.8],
  [-0.33, -0.34, 0.28, 0.6],
  [0.33, -0.32, 0.28, 0.6],
  [0.71, -0.10, 0.31, 0.8],
  [-0.58, 0.42, 0.33, 0.7],
  [-0.20, 0.24, 0.29, 0.5],
  [0.24, 0.26, 0.29, 0.5],
  [0.62, 0.38, 0.32, 0.7],
  [-0.22, 0.68, 0.32, 0.8],
  [0.24, 0.66, 0.33, 0.8],
  [0.01, 0.44, 0.29, 0.5],
  [0.00, -0.16, 0.42, 0.35],
]

/** Fugen-Treffpunkte (wo 2-3 Noppen sich beruehren) fuer die Schatten-Glut */
const CREVICES: ReadonlyArray<readonly [number, number]> = [
  [-0.42, -0.16], [0.44, -0.12], [-0.35, 0.42], [0.37, 0.40], [0.0, -0.52], [0.02, 0.62],
]

export const paintBrombeere: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  const outlineW = Math.max(2, r * 0.12)
  // Schatten-Gluehen zwischen den Noppen: 3★ = Dauerglut, castPulse = Burst
  const glowA = dead ? 0 : Math.min(1, (tier >= 3 ? 0.5 : 0) + pose.castPulse * 0.6)

  ctx.save()
  ctx.lineJoin = 'round'
  ctx.translate(x, y)
  ctx.rotate(0.05) // minimale Schieflage — lauernder, leicht bedrohlicher Stand

  /** Union aller Noppen-Kreise (optional expandiert) als aktueller Pfad */
  const unionPath = (inset = 0): void => {
    ctx.beginPath()
    for (const [dx, dy, br] of DRUPELETS) {
      ctx.moveTo(dx * r + br * r + inset, dy * r)
      ctx.arc(dx * r, dy * r, br * r + inset, 0, Math.PI * 2)
    }
  }

  // --- 2★: Dornbusch-Dornen (hinter dem Cluster, brechen die Silhouette) ---
  if (tier >= 2) {
    ctx.fillStyle = darken(colors.accent, 30)
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    for (const [tx, ty, ang] of [[-0.82, -0.18, -2.6], [0.84, -0.24, -0.5], [0.62, -0.78, -1.0]] as const) {
      ctx.save()
      ctx.translate(tx * r, ty * r)
      ctx.rotate(ang)
      ctx.beginPath()
      ctx.moveTo(0, -r * 0.1)
      ctx.quadraticCurveTo(r * 0.26, -r * 0.12, r * 0.34, -r * 0.02) // Aussenkante zur Spitze
      ctx.quadraticCurveTo(r * 0.2, r * 0.04, 0, r * 0.1) // gebogene Innenkante zurueck
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      ctx.restore()
    }
  }

  // --- Silhouette: Outline-Unterlage (Union aller Noppen, expandiert) ---
  ctx.fillStyle = colors.outline
  unionPath(outlineW * 0.5)
  ctx.fill()

  // --- Fugen-Basis: dunkelster Ton, bleibt ZWISCHEN den Noppen sichtbar ---
  ctx.fillStyle = darken(colors.bodyDark, 16)
  unionPath()
  ctx.fill()

  // --- Innenleben, geclippt auf die Union ---
  ctx.save()
  unionPath()
  ctx.clip()

  // Schatten-Glut auf der Fugen-Basis — scheint nur durch die Zwischenraeume
  if (glowA > 0) {
    glow(ctx, 0, r * 0.02, r * 1.08, SHADOW, Math.min(0.95, glowA * 1.25))
  }

  // Noppen: pro Kugel Kontakt-Schatten -> Volumen -> Zellen-Rand -> Licht
  for (const [i, [dx, dy, br, gloss]] of DRUPELETS.entries()) {
    const bx = dx * r
    const by = dy * r
    const bR = br * r
    // Kontakt-Schatten nach unten-rechts (Tiefe gegenueber den Nachbar-Noppen)
    ctx.fillStyle = rgba(darken(colors.bodyDark, 20), 0.5 * (1 - glowA * 0.7))
    ctx.beginPath()
    ctx.arc(bx + bR * 0.12, by + bR * 0.14, bR, 0, Math.PI * 2)
    ctx.fill()
    // Kugel mit Volumen — leicht kleiner als die Union, damit die Fuge offen bleibt
    ctx.fillStyle = volumeGradient(ctx, bx, by, bR, colors.body)
    ctx.beginPath()
    ctx.arc(bx, by, bR * 0.93, 0, Math.PI * 2)
    ctx.fill()
    // feiner Zellen-Rand (Noppen-Trennung, kein hartes Schwarz)
    ctx.strokeStyle = rgba(colors.outline, 0.35)
    ctx.lineWidth = Math.max(1, r * 0.03)
    ctx.stroke()
    // gluehender Fugen-Ring an der eigenen Noppen-Kante — spaeter gezeichnete
    // Noppen verdecken ihn korrekt, es gluehen also genau die sichtbaren Fugen
    if (glowA > 0) {
      ctx.save()
      ctx.strokeStyle = rgba(SHADOW, glowA * 0.45)
      ctx.lineWidth = Math.max(1, r * 0.035)
      ctx.shadowColor = SHADOW
      ctx.shadowBlur = r * (0.06 + pose.castPulse * 0.08)
      ctx.beginPath()
      ctx.arc(bx, by, bR * 0.97, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    }
    // Rim-Kante nur auf lichtzugewandten Noppen (oben-links); Gesichts-Noppe ruhig
    const w = Math.max(0, Math.min(1, -dx - dy + 0.25))
    if (!dead && w > 0.05 && i < DRUPELETS.length - 1) {
      ctx.strokeStyle = rgba(lighten(colors.body, 32), 0.55 * w)
      ctx.lineWidth = Math.max(1, r * 0.035)
      ctx.beginPath()
      ctx.arc(bx, by, bR * 0.78, Math.PI * 1.02, Math.PI * 1.6)
      ctx.stroke()
    }
    // EIN Glanzpunkt pro Noppe — Beeren-Textur-Signatur (nie auf Toten)
    if (!dead) {
      specular(ctx, bx - bR * 0.32, by - bR * 0.36, bR * 0.24, 0.25 + 0.4 * gloss)
    }
  }

  // Glut-Hotspots an den Fugen-Treffpunkten — Licht leckt aus den Ritzen
  if (glowA > 0) {
    for (const [cxr, cyr] of CREVICES) {
      glow(ctx, cxr * r, cyr * r, r * (0.13 + pose.castPulse * 0.05), SHADOW, glowA * 0.85)
    }
  }
  ctx.restore() // Clip Ende

  // --- Hexen-Kelch: verdrehter Stiel-Haken + 5 gezackte Sepal-Krallen oben ---
  ctx.save()
  ctx.translate(-r * 0.02, -r * 0.92)
  ctx.rotate(-0.08)
  // Stiel-Haken zuerst (hinter den Sepalen): Outline-Unterlage -> Kern -> Licht-Kante
  ctx.lineCap = 'round'
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(2.2, r * 0.15)
  ctx.beginPath()
  ctx.moveTo(0, -r * 0.02)
  ctx.quadraticCurveTo(-r * 0.02, -r * 0.24, r * 0.2, -r * 0.3)
  ctx.stroke()
  ctx.strokeStyle = darken(colors.accent, 34)
  ctx.lineWidth = Math.max(1.6, r * 0.1)
  ctx.stroke()
  if (!dead) {
    ctx.strokeStyle = rgba(lighten(colors.accent, 16), 0.7)
    ctx.lineWidth = Math.max(1, r * 0.035)
    ctx.beginPath()
    ctx.moveTo(-r * 0.015, -r * 0.08)
    ctx.quadraticCurveTo(-r * 0.02, -r * 0.22, r * 0.14, -r * 0.27)
    ctx.stroke()
  }
  // Sepal-Krallen: schmale gebogene Zacken, krallen sich ueber die oberste Noppe
  const sg = ctx.createLinearGradient(0, -r * 0.06, 0, r * 0.3)
  sg.addColorStop(0, darken(colors.accent, 22))
  sg.addColorStop(1, darken(colors.accent, 48))
  ctx.fillStyle = sg
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(1.2, r * 0.05)
  for (const [ang, len] of [[2.75, 0.3], [2.2, 0.26], [1.57, 0.24], [0.95, 0.26], [0.4, 0.3]] as const) {
    const tx = Math.cos(ang) * len * r
    const ty = Math.sin(ang) * len * r
    const px = Math.cos(ang + Math.PI / 2)
    const py = Math.sin(ang + Math.PI / 2)
    const bw = r * 0.075
    ctx.beginPath()
    ctx.moveTo(px * bw, py * bw)
    ctx.quadraticCurveTo(tx * 0.45 + px * bw * 1.1, ty * 0.45 + py * bw * 1.1, tx, ty)
    ctx.quadraticCurveTo(tx * 0.45 - px * bw * 1.1, ty * 0.45 - py * bw * 1.1, -px * bw, -py * bw)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
  }
  // Kelch-Knubbel in der Mitte
  ctx.fillStyle = darken(colors.accent, 26)
  ctx.beginPath()
  ctx.arc(0, 0, r * 0.09, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()
  ctx.restore()

  ctx.restore()
}
